/**
 * Barcode scanning: the browser's BarcodeDetector where it exists, a ZXing
 * fallback loaded on demand where it does not, and typing always works.
 * Scanning is an accelerator, never a gate.
 */
import { useEffect, useRef, useState } from "react";
import { Button, Icon, Modal } from "@crc/ui";

type Detector = { detect: (src: ImageBitmapSource) => Promise<Array<{ rawValue: string }>> };
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => Detector;
const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "qr_code"];

async function makeDetector(): Promise<Detector | null> {
  const w = window as unknown as { BarcodeDetector?: BarcodeDetectorCtor };
  if (w.BarcodeDetector) return new w.BarcodeDetector({ formats: FORMATS });
  try {
    // Fallback: ZXing's pure-JS/WASM reader, loaded only when needed and only if reachable.
    const url = "https://cdn.jsdelivr.net/npm/@zxing/browser@0.1.5/+esm";
    const mod = (await import(/* @vite-ignore */ url)) as { BrowserMultiFormatReader: new () => { decodeFromCanvas: (c: HTMLCanvasElement) => { getText: () => string } } };
    const reader = new mod.BrowserMultiFormatReader();
    return {
      detect: async (src) => {
        const canvas = src instanceof HTMLCanvasElement ? src : null;
        if (!canvas) return [];
        try { return [{ rawValue: reader.decodeFromCanvas(canvas).getText() }]; } catch { return []; }
      },
    };
  } catch {
    return null;
  }
}

export function BarcodeScanButton({ onScan, label = "Scan" }: { onScan: (code: string) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  if (!supported) return null;
  return (
    <>
      <Button size="sm" icon="search" onClick={() => setOpen(true)} title="Scan a barcode with the camera">{label}</Button>
      {open && <ScannerModal onClose={() => setOpen(false)} onScan={(c) => { onScan(c); setOpen(false); }} />}
    </>
  );
}

function ScannerModal({ onClose, onScan }: { onClose: () => void; onScan: (c: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<"starting" | "scanning" | "unsupported" | "denied">("starting");
  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    (async () => {
      const detector = await makeDetector();
      if (!detector) { setStatus("unsupported"); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      } catch { setStatus("denied"); return; }
      if (stopped || !video.current) return;
      video.current.srcObject = stream;
      await video.current.play().catch(() => undefined);
      setStatus("scanning");
      const canvas = document.createElement("canvas");
      const tick = async () => {
        if (stopped || !video.current) return;
        try {
          const v = video.current;
          if (v.videoWidth) {
            canvas.width = v.videoWidth;
            canvas.height = v.videoHeight;
            canvas.getContext("2d")?.drawImage(v, 0, 0);
            const found = await detector.detect(canvas);
            if (found[0]?.rawValue) { onScan(found[0].rawValue); return; }
          }
        } catch { /* keep scanning */ }
        raf = window.setTimeout(tick, 250);
      };
      tick();
    })();
    return () => { stopped = true; window.clearTimeout(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [onScan]);
  return (
    <Modal open onClose={onClose} title="Scan a barcode" footer={<Button onClick={onClose}>Cancel — I will type it</Button>}>
      {status === "unsupported" && <p className="muted">This browser cannot decode barcodes and the fallback reader could not load. Type the number instead — it works exactly the same.</p>}
      {status === "denied" && <p className="muted">Camera access was declined. Type the number instead.</p>}
      <div style={{ position: "relative", background: "var(--bg)", borderRadius: "var(--r-md)", overflow: "hidden", minHeight: 200 }}>
        <video ref={video} muted playsInline style={{ width: "100%", display: status === "scanning" ? "block" : "none" }} />
        {status === "starting" && <div className="p-4 muted flex" style={{ gap: 8, alignItems: "center" }}><Icon name="clock" /> Starting camera…</div>}
      </div>
      <p className="small muted mt-3">Hold the code steady in the frame. Unknown codes start an ingredient request — they are never an error.</p>
    </Modal>
  );
}
